/**
 * ============================================================================
 * CAPA 纠正预防措施 API 工厂函数
 * ============================================================================
 *
 * 📋 文件职责：
 * - 定义 CAPA（Corrective and Preventive Action）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 路由常量统一取自 API_ROUTES.quality.capa*
 *
 * @module api/quality/capa-factory
 * @description CAPA（纠正预防措施）模块的 API 工厂函数
 * @author YSFL Team
 * @created 2026-09-19
 * ============================================================================
 */

import type {
  Capa,
  CapaListItem,
  CapaStatistics,
  CapaAction,
  CapaLog,
  CapaQueryParams,
  CapaActionForm,
  CreateCapaForm,
  UpdateCapaForm,
  UpdateCapaActionForm,
  VerifyCapaForm,
} from '../../types/quality/capa'
import type { PaginationResult } from '../../types/api/responses'
import type { IRequestClient } from '../core/client'
import { API_ROUTES } from '../routes'

// ============================================================================
// 响应结构
// ============================================================================

/** 枚举项 */
export interface CapaEnumItem {
  value: string
  label: string
}

// ============================================================================
// 工厂函数
// ============================================================================

/**
 * 创建 CAPA 纠正预防措施 API 实例
 *
 * @param client - HTTP 请求客户端
 * @returns CAPA API 实例
 */
export function createCapaApi(client: IRequestClient) {
  const routes = API_ROUTES.quality

  return {
    // ========================================================================
    // 枚举
    // ========================================================================

    /** 获取CAPA状态枚举 */
    getStatuses() {
      return client.get<CapaEnumItem[]>(routes.capaStatuses)
    },

    /** 获取来源类型枚举 */
    getSourceTypes() {
      return client.get<CapaEnumItem[]>(routes.capaSourceTypes)
    },

    /** 获取行动项执行状态枚举 */
    getActionStatuses() {
      return client.get<CapaEnumItem[]>(routes.capaActionStatuses)
    },

    // ========================================================================
    // 查询
    // ========================================================================

    /** 分页获取CAPA列表 */
    getCapaList(params?: CapaQueryParams) {
      return client.get<PaginationResult<CapaListItem>>(routes.capaList, { params })
    },

    /** 我的CAPA（与我相关的未关闭单据） */
    getMyCapas(params?: { page?: number; per_page?: number }) {
      return client.get<PaginationResult<CapaListItem>>(routes.capaMy, { params })
    },

    /** 统计分析 */
    getStatistics(params?: { start_date?: string; end_date?: string }) {
      return client.get<CapaStatistics>(routes.capaStatistics, { params })
    },

    /** CAPA详情（含行动项与日志） */
    getCapaDetail(id: string | number) {
      return client.get<Capa>(routes.capaDetail(id))
    },

    /** CAPA操作日志 */
    getCapaLogs(id: string | number) {
      return client.get<{ items: CapaLog[] }>(routes.capaLogs(id))
    },

    // ========================================================================
    // 创建 / 编辑 / 删除
    // ========================================================================

    /** 创建CAPA单（可带行动项） */
    createCapa(data: CreateCapaForm) {
      return client.post<Capa>(routes.capaList, data)
    },

    /** 更新CAPA单（仅草稿） */
    updateCapa(id: string | number, data: UpdateCapaForm) {
      return client.put<Capa>(routes.capaUpdate(id), data)
    },

    /** 删除CAPA单（仅草稿） */
    deleteCapa(id: string | number) {
      return client.delete<void>(routes.capaDelete(id))
    },

    // ========================================================================
    // 行动项
    // ========================================================================

    /** 新增行动项 */
    addAction(capaId: string | number, data: CapaActionForm) {
      return client.post<CapaAction>(routes.capaActions(capaId), data)
    },

    /** 更新行动项（含进度填报） */
    updateAction(capaId: string | number, actionId: string | number, data: UpdateCapaActionForm) {
      return client.put<CapaAction>(routes.capaAction(capaId, actionId), data)
    },

    /** 删除行动项 */
    removeAction(capaId: string | number, actionId: string | number) {
      return client.delete<void>(routes.capaAction(capaId, actionId))
    },

    // ========================================================================
    // 状态流转
    // ========================================================================

    /** 提交处理（草稿→处理中） */
    submitCapa(id: string | number) {
      return client.post<Capa>(routes.capaSubmit(id))
    },

    /** 提交验证（处理中→待验证，要求全部行动项已完成） */
    submitVerification(id: string | number) {
      return client.post<Capa>(routes.capaSubmitVerification(id))
    },

    /** 有效性验证（通过=关闭；退回=回处理中，必须填原因） */
    verifyCapa(id: string | number, data: VerifyCapaForm) {
      return client.post<Capa>(routes.capaVerify(id), data)
    },

    /** 取消CAPA单 */
    cancelCapa(id: string | number, reason?: string) {
      return client.post<Capa>(routes.capaCancel(id), { reason })
    },
  }
}
